import { useMemo } from "react";

import { FeatureFlag } from "featureFlags/constants";
import { useAllFlags } from "featureFlags/hooks/useAllFlags";
import { useHasAdminRole } from "services/auth/useHasAdminRole";
import { useOrganizationSettings } from "services/organization/useOrganizationSettings";

export enum AiSurfaceState {
  Hidden = "Hidden",
  Enabled = "Enabled",
  AdminCta = "AdminCta", // only for chat surface
}

export enum AiToggleVisibility {
  Hidden = "Hidden",
  Visible = "Visible",
}

export type AiSurfacePolicy = {
  chat: AiSurfaceState; // Enabled | AdminCta | Hidden
  editor: AiSurfaceState; // Enabled | Hidden
  orgSettingsToggle: AiToggleVisibility; // Visible when admin and any AI flag is on
};

export const useAiAccessPolicy = (): AiSurfacePolicy => {
  const flags = useAllFlags();
  const { organizationSettings } = useOrganizationSettings();
  const isOrgAdmin = useHasAdminRole();

  const chatFlag = !!flags[FeatureFlag.FireboltAppEnableChatBot];
  const editorFlag = !!flags[FeatureFlag.FireboltAppEnableAiInEditor];
  const enableAi = organizationSettings.firebolt_agent_enabled;

  return useMemo<AiSurfacePolicy>(() => {
    let chat: AiSurfaceState = AiSurfaceState.Hidden;
    if (chatFlag && enableAi) {
      chat = AiSurfaceState.Enabled;
    } else if (isOrgAdmin && chatFlag && !enableAi) {
      chat = AiSurfaceState.AdminCta;
    }

    const editor: AiSurfaceState =
      editorFlag && enableAi ? AiSurfaceState.Enabled : AiSurfaceState.Hidden;

    const orgSettingsToggle: AiToggleVisibility =
      isOrgAdmin && (chatFlag || editorFlag)
        ? AiToggleVisibility.Visible
        : AiToggleVisibility.Hidden;

    return { chat, editor, orgSettingsToggle };
  }, [chatFlag, editorFlag, enableAi, isOrgAdmin]);
};
